import { useQuery } from "@apollo/client/react";
import { Breadcrumbs, Button, Stack, styled, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { EyeIcon } from "@phosphor-icons/react/dist/csr/Eye";
import { format, getMonth, getYear, subMonths } from "date-fns";
import { Dispatch, ReactNode, SetStateAction, useState } from "react";
import { SetURLSearchParams, useSearchParams } from "react-router-dom";
import { hasPermission } from "../../../components";
import { DependencyTable } from "../../../components/DependencyTable/DependencyTable";
import { DOWNLOAD_CHECKBOX_OPTIONS } from "../../../components/DownloadsDataGrid/DownloadFilters";
import {
  DownloadsDataGridWithQuery,
  initialVisibilityModelDownloadsDatagrid,
} from "../../../components/DownloadsDataGrid/DownloadsDataGridWithQuery";
import { EventsDataGridWithQuery } from "../../../components/EventsDataGrid/EventsDataGridWithQuery";
import { MiddlePageLoading } from "../../../components/MiddlePageLoading";
import { UPLOAD_CHECKBOX_OPTIONS } from "../../../components/UploadsDataGrid/UploadFilters";
import {
  initialVisibilityModelUploadsDatagrid,
  UploadsDataGridWithQuery,
} from "../../../components/UploadsDataGrid/UploadsDataGridWithQuery";
import Permissions from "../../../constants/permissions";
import useAuth, { DataGridColumns, DataGrids } from "../../../context/UserContext";
import { DOWNLOAD_DETAILS, UPLOAD_DETAILS } from "../../../graphql/queries/homepage";
import {
  HomepageSparklineDay,
  DownloadDetailsDocument,
  DownloadDetailsQuery,
  DownloadDetailsQueryVariables,
  UploadDetailsDocument,
  UploadDetailsQuery,
  UploadDetailsQueryVariables,
} from "../../../graphql/types";
import { useNavigator } from "../../../lib/Hooks";
import { CheckboxFilters } from "../../keys/components/CheckboxFilters";
import { CalendarWithMonths } from "../components/CalendarWithMonths";
import { CardInfo } from "../components/CardInfo";

const StyledEye = styled(EyeIcon)(({ theme }) => ({
  color: theme.palette.text50.main,
}));

const EXCLUDED_FIELDS = [DataGridColumns.Project, DataGridColumns.Name];

const ToggleButtons = ({
  tableToShow,
  setTableToShow,
  setSearchParams,
  name,
  type,
}: {
  tableToShow: "download" | "upload" | "event";
  setTableToShow: (tableToShow: "download" | "upload" | "event") => void;
  setSearchParams: SetURLSearchParams;
  name: string;
  type: "download" | "upload";
}) => {
  return (
    <ToggleButtonGroup
      color="secondary"
      value={tableToShow}
      exclusive
      onChange={(_, value) => {
        setTableToShow(value);
        setSearchParams({ name, type });
      }}
    >
      {type === "download" ? (
        <ToggleButton
          sx={{
            height: "28px",
            marginRight: "-8px",
            width: "134px",
          }}
          size="xSmall"
          value="download"
        >
          Downloads
        </ToggleButton>
      ) : (
        <ToggleButton
          sx={{
            height: "28px",
            marginRight: "-8px",
            width: "134px",
          }}
          size="xSmall"
          value="upload"
        >
          Uploads
        </ToggleButton>
      )}
      <ToggleButton
        sx={{
          height: "28px",
          width: "134px",
        }}
        size="xSmall"
        value="event"
      >
        Events
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

const CommonDetails = ({
  name,
  type,
  tableToShow,
  setTableToShow,
  sparkline,
  sparklineFirstDate,
  cardInfoAndDependencyTable,
  checkAllText,
  checkboxOptions,
  initialVisibilityModel,
  preferenceKey,
}: {
  name: string;
  type: "download" | "upload";
  tableToShow: "download" | "upload" | "event";
  setTableToShow: Dispatch<SetStateAction<"download" | "event" | "upload">>;
  sparkline: HomepageSparklineDay[];
  sparklineFirstDate: string;
  cardInfoAndDependencyTable: ReactNode;
  checkAllText: string;
  checkboxOptions: Array<{ value: DataGridColumns; label: string }>;
  initialVisibilityModel: Partial<Record<DataGridColumns, boolean>>;
  preferenceKey: DataGrids;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigator = useNavigator();
  const { user, updateDataGridPreferences } = useAuth();
  const [day, setDay] = useState<number>(new Date().getDate());
  const [month, setMonth] = useState<number>(new Date().getMonth());
  const onClickDay = (day: number, month: number) => {
    setDay(day);
    setMonth(month);
  };
  const onClickButton = () => {
    let goto = "";
    switch (tableToShow) {
      case "download":
        goto = "downloads/list";
        break;
      case "upload":
        goto = "uploads/list";
        break;
      case "event":
        goto = "events";
        break;
      default:
        goto = "";
    }
    // preserve all parameters except name and type
    const params: Record<string, string> = {};
    for (const [key, value] of searchParams) {
      if (key !== "name" && key !== "type") {
        params[key] = value;
      }
    }

    navigator.goto(goto, params);
  };

  const datagridPreferencesElement = user.datagridPreferences[preferenceKey];
  const visibilityModel = datagridPreferencesElement?.columnVisibilityModel || initialVisibilityModel;

  const getSelectedOptions = () => {
    return Object.entries(visibilityModel)
      .filter(([, value]) => value)
      .map(([key]) => key);
  };

  const handleColumnVisibilityChange = (value: DataGridColumns) => {
    updateDataGridPreferences(preferenceKey, {
      columnsOrder: datagridPreferencesElement?.columnsOrder,
      columnsWidth: datagridPreferencesElement?.columnsWidth,
      columnVisibilityModel: {
        ...visibilityModel,
        [value]: !visibilityModel[value],
      },
    });
  };

  let DataGrid: ReactNode;
  if (tableToShow === "download") {
    DataGrid = (
      <DownloadsDataGridWithQuery
        excludeFields={["project", "name"]}
        variables={{
          bundleNames: [name],
          reportRange: {
            start: new Date(new Date().getFullYear(), month, day),
            end: new Date(new Date().getFullYear(), month, day),
          },
        }}
        useDefaultDateRange={false}
        disableDataGridFilter={true}
      />
    );
  } else if (tableToShow === "upload") {
    DataGrid = (
      <UploadsDataGridWithQuery
        excludeFields={["project", "name"]}
        variables={{
          reportRange: {
            start: new Date(new Date().getFullYear(), month, day),
            end: new Date(new Date().getFullYear(), month, day),
          },
          exportNames: [name],
        }}
        useDefaultDateRange={false}
        disableDataGridFilter={true}
      />
    );
  } else {
    DataGrid = (
      <EventsDataGridWithQuery
        variables={{
          reportRange: {
            start: new Date(new Date().getFullYear(), month, day),
            end: new Date(new Date().getFullYear(), month, day),
          },
          downloadName: type === "download" ? [name] : undefined,
          uploadName: type === "upload" ? [name] : undefined,
        }}
        disableDataGridFilter={true}
        useDefaultDateRange={false}
      />
    );
  }
  return (
    <Stack sx={{ gap: "32px" }}>
      <Breadcrumbs>
        <Typography
          variant="body.overline.semibold"
          onClick={() => {
            navigator.goto("home");
          }}
          sx={{
            "&:hover": {
              cursor: "pointer",
            },
          }}
        >
          Home
        </Typography>
        <Typography variant="body.overline.semibold">{name}</Typography>
      </Breadcrumbs>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "3fr 1fr",
          gap: "24px",
        }}
      >
        <Stack sx={{ gap: "24px" }}>{cardInfoAndDependencyTable}</Stack>
        <CalendarWithMonths
          onClickDay={onClickDay}
          selectedDay={day}
          selectedMonth={month}
          sparkline={sparkline}
          sparklineFirstDate={sparklineFirstDate}
          leftMonth={getMonth(subMonths(new Date(), 1))}
          rightMonth={getMonth(new Date())}
          year={getYear(new Date())}
        />
      </div>
      <Stack sx={{ gap: "19px" }}>
        <Typography variant="body.large.semibold">
          {format(new Date(new Date().getFullYear(), month, day), "MMMM d")}
        </Typography>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <ToggleButtons
            tableToShow={tableToShow}
            setTableToShow={setTableToShow}
            setSearchParams={setSearchParams}
            name={name}
            type={type}
          />
          <Stack direction="row" sx={{ gap: "8px", alignItems: "center" }}>
            {tableToShow !== "event" && (
              <CheckboxFilters
                Icon={<StyledEye size={24} />}
                options={checkboxOptions.filter((option) => {
                  if (EXCLUDED_FIELDS.includes(option.value)) return false;
                  if (
                    option.value === DataGridColumns.Actions &&
                    !hasPermission(Permissions.ManageAccountSettings, user)
                  )
                    return false;
                  return true;
                })}
                onClickOption={handleColumnVisibilityChange}
                selectedOptions={getSelectedOptions()}
              />
            )}
            <Button variant="text" color="secondary" onClick={onClickButton}>
              {checkAllText}
            </Button>
          </Stack>
        </Stack>
        {DataGrid}
      </Stack>
    </Stack>
  );
};

const DownloadDetails = ({ name }: { name: string }) => {
  const { data, loading, refetch, client } = useQuery<DownloadDetailsQuery, DownloadDetailsQueryVariables>(
    DownloadDetailsDocument,
    {
      variables: {
        downloadName: name,
      },
    }
  );
  const [refreshing, setRefreshing] = useState(false);

  const refreshData = async () => {
    setRefreshing(true);

    const { data: newData } = await refetch({ downloadName: name, refresh: true });
    client.writeQuery({
      query: DOWNLOAD_DETAILS,
      variables: {
        downloadName: name,
      },
      data: newData,
    });
    setRefreshing(false);
  };

  const [tableToShow, setTableToShow] = useState<"download" | "event" | "upload">("download");
  const checkAllText = tableToShow === "download" ? "View in Downloads list" : "View in Events list";

  if (loading) {
    return <MiddlePageLoading />;
  }
  const downloadDetailsResult = data?.downloadDetails;
  return (
    <CommonDetails
      name={name}
      type="download"
      tableToShow={tableToShow}
      setTableToShow={setTableToShow}
      sparkline={downloadDetailsResult?.sparkline || []}
      sparklineFirstDate={downloadDetailsResult?.sparklineFirstDate || ""}
      cardInfoAndDependencyTable={
        <>
          <CardInfo
            name={name}
            status={downloadDetailsResult?.status || ""}
            type="download"
            position={downloadDetailsResult?.pos}
            scriptbookId={downloadDetailsResult?.scriptbookId || ""}
            refreshData={refreshData}
            refreshing={refreshing}
          />
          <DependencyTable
            downloads={downloadDetailsResult?.uploadsUsingDownload}
            keys={downloadDetailsResult?.keysUsedByDownload}
          />
        </>
      }
      checkAllText={checkAllText}
      checkboxOptions={DOWNLOAD_CHECKBOX_OPTIONS}
      initialVisibilityModel={initialVisibilityModelDownloadsDatagrid}
      preferenceKey="downloads"
    />
  );
};

const UploadDetails = ({ name }: { name: string }) => {
  const { data, loading, refetch, client } = useQuery<UploadDetailsQuery, UploadDetailsQueryVariables>(
    UploadDetailsDocument,
    {
      variables: {
        uploadName: name,
      },
    }
  );
  const [refreshing, setRefreshing] = useState(false);

  const refreshData = async () => {
    setRefreshing(true);
    const { data: newData } = await refetch({ uploadName: name, refresh: true });
    client.writeQuery({
      query: UPLOAD_DETAILS,
      variables: {
        uploadName: name,
      },
      data: newData,
    });
    setRefreshing(false);
  };

  const [tableToShow, setTableToShow] = useState<"download" | "event" | "upload">("upload");
  const checkAllText = tableToShow === "upload" ? "View in Uploads list" : "View in Events list";

  if (loading) {
    return <MiddlePageLoading />;
  }
  const uploadDetailsResult = data?.uploadDetails;
  return (
    <CommonDetails
      name={name}
      type="upload"
      tableToShow={tableToShow}
      setTableToShow={setTableToShow}
      sparkline={uploadDetailsResult?.sparkline || []}
      sparklineFirstDate={uploadDetailsResult?.sparklineFirstDate || ""}
      cardInfoAndDependencyTable={
        <>
          <CardInfo
            name={name}
            status={uploadDetailsResult?.status || ""}
            type="upload"
            position={uploadDetailsResult?.pos}
            scriptbookId={uploadDetailsResult?.scriptbookId || ""}
            refreshData={refreshData}
            refreshing={refreshing}
          />
          <DependencyTable
            uploads={uploadDetailsResult?.downloadsUsedByUpload}
            keys={uploadDetailsResult?.keysUsedByUpload}
          />
        </>
      }
      checkAllText={checkAllText}
      checkboxOptions={UPLOAD_CHECKBOX_OPTIONS}
      initialVisibilityModel={initialVisibilityModelUploadsDatagrid}
      preferenceKey="uploads"
    />
  );
};

const HomepageDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name");
  const type = searchParams.get("type");

  if (!name || !type || (type !== "download" && type !== "upload")) {
    return <div>Invalid URL</div>;
  }
  return <>{type === "download" ? <DownloadDetails name={name} /> : <UploadDetails name={name} />}</>;
};

export { HomepageDetailsPage };
